import { atom, selector } from 'recoil';
import { AnnotationRecord } from '~/domain/annotation/annotation';
import {
    CircleRegion,
    CircleRegionDraft,
} from '~/domain/intensity/circleRegion';
import { LineProfile, LineProfileDraft } from '~/domain/intensity/lineProfile';
import {
    AnnotationListState,
    annotationListState,
} from '~/state/atoms/annotationListAtoms';
import { KEEP_LATEST_ONLY } from '~/state/atoms/selectorCachePolicy';

/**
 * State of the intensity tool: which shape the next click places, the
 * lines and circles placed so far, and what is selected, highlighted or
 * being dragged. The line and circle types themselves are in `domain/`.
 */

/** The two shapes the intensity tool can place. */
export type IntensityShape = 'line' | 'circle';

/**
 * The shape the intensity tool will place on the next click. A line reads
 * the intensity along its length and plots it; a circle reads the intensity
 * inside it and gives min, max, mean and SD. They are two shapes of one
 * tool because both read the same quantity.
 */
export const intensityShapeState = atom<IntensityShape>({
    key: 'intensityShapeState',
    default: 'line',
});

/** The placed line profiles, one atom each, oldest first. */
export const lineProfileListState =
    annotationListState<LineProfile>('lineProfile');

/** The line being placed right now, or null when none is. */
export const lineProfileDraftState = atom<LineProfileDraft | null>({
    key: 'lineProfileDraftState',
    default: null,
});

/** Id of the line highlighted on the image while its row is hovered, or null. */
export const highlightedLineProfileState = atom<string | null>({
    key: 'highlightedLineProfileState',
    default: null,
});

/**
 * Id of the line or circle whose detail the panel shows; null means the
 * newest one of the shape being listed. One atom serves both shapes because
 * their ids carry a shape prefix and cannot collide. Measurements have their
 * own selection state.
 */
export const selectedIntensityAnnotationState = atom<string | null>({
    key: 'selectedIntensityAnnotationState',
    default: null,
});

/**
 * Id of the line whose endpoint is being dragged, or null. While it is set,
 * the panel keeps the last comparison instead of re-sampling every dataset
 * on each mouse move.
 */
export const draggingLineProfileState = atom<string | null>({
    key: 'draggingLineProfileState',
    default: null,
});

/** The placed circles, one atom each, oldest first. */
export const circleRegionListState =
    annotationListState<CircleRegion>('circleRegion');

/** The circle being placed right now, or null when none is. */
export const circleRegionDraftState = atom<CircleRegionDraft | null>({
    key: 'circleRegionDraftState',
    default: null,
});

/** Id of the circle highlighted on the image while its row is hovered, or null. */
export const highlightedCircleRegionState = atom<string | null>({
    key: 'highlightedCircleRegionState',
    default: null,
});

/**
 * True while a line or a circle is being placed. No line or circle can be
 * edited meanwhile: the placement expects the next click, and a handle on
 * another annotation would take that click and start a drag instead.
 */
export const intensityPlacementActiveState = selector<boolean>({
    key: 'intensityPlacementActiveState',
    get: ({ get }) =>
        get(lineProfileDraftState) !== null ||
        get(circleRegionDraftState) !== null,
    cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
});

/**
 * The annotation of one shape whose detail the panel shows: the selected one
 * when it is a placed annotation of that shape, else the newest of the shape.
 */
function shownAnnotationState<T extends AnnotationRecord>(
    key: string,
    list: AnnotationListState<T>
) {
    return selector<T | undefined>({
        key,
        get: ({ get }) => {
            const ids = get(list.ids);
            const selectedId = get(selectedIntensityAnnotationState);
            const shownId: string | undefined =
                selectedId !== null && ids.includes(selectedId)
                    ? selectedId
                    : ids[ids.length - 1];
            return shownId === undefined ? undefined : get(list.item(shownId));
        },
        cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
    });
}

/** The line whose detail the panel shows; undefined when none is placed. */
export const shownLineProfileState = shownAnnotationState(
    'shownLineProfileState',
    lineProfileListState
);

/** The circle whose detail the panel shows; undefined when none is placed. */
export const shownCircleRegionState = shownAnnotationState(
    'shownCircleRegionState',
    circleRegionListState
);
